import { Skeleton } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../common/Button';
import CustomModal from '../../../common/Modal';
import { Animal } from '../../../contexts/Staking/types';
import { useStack } from '../../../hooks/useStaking';
import { ButtonGroup, MultiplierBox, NftImg, NftRoot, Title } from './styles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime'; // import plugin
import 'dayjs/locale/en' ;
import { useWallet } from '@solana/wallet-adapter-react';

dayjs.extend(relativeTime); // use plugin
dayjs.locale('en'); // use locale

interface Props {
  token: Animal;
  isStaked?: boolean;
}

const NftCard = ({ token, isStaked }: Props) => {
  const {
    avaliableStakedAnimals,
    setAvaliableStakedAnimals,
    getPendingStakingRewards,
    stakeAnimal,
    unstakeAnimal,
    claimStakingRewards,
  } = useStack();

  const wallet = useWallet();
  const [stakingPeriod, setStakingPeriod] = useState<Date>(new Date());
  const [redeemable, setRedeemable] = useState<number>(0);
  const [isStakeModalOpen, setStakeModalIsOpen] = useState(false);
  const [isClaimModalOpen, setClaimModalIsOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [timeToUnlock, setTimeToUnlock] = useState("");

  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }
    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    };
  }, [wallet]);

  const checkLock = () => {
    if (token?.lastClaim) {
      if (anchorWallet) {
        const lsLastClaim = localStorage.getItem(anchorWallet.publicKey.toString());
        const lastClaim = lsLastClaim ? new Date(lsLastClaim) : undefined;
        if (lastClaim && dayjs(token?.lastClaim).diff(dayjs(lastClaim)) <= 0) {
          const endDate = dayjs(lastClaim).add(3, "days");
          setTimeToUnlock(dayjs().to(endDate));
          const days = dayjs(endDate).diff(dayjs());
          if (days <= 0) setIsLocked(false);
          else setIsLocked(true);
        } else {
          const endDate = dayjs(token?.lastClaim).add(3, "days");
          const days = dayjs(endDate).diff(dayjs());
          setTimeToUnlock(dayjs().to(endDate));
          if (days <= 0) setIsLocked(false);
          else setIsLocked(true);
        }
      }
    }
  }

  useEffect(() => {
    checkLock();
  },[token?.lastClaim]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (token) {
        setStakingPeriod(new Date());
        checkLock();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [token, getPendingStakingRewards, setStakingPeriod]);

  // handle Stake Nft
  const handleStake = useCallback(async () => {
    if (!token) return;
    await stakeAnimal(token);
  }, [token, stakeAnimal]);

  // handle Unstake NFt
  const handleUnstake = useCallback(async () => {
    if (!token) return;
    await unstakeAnimal(token);
  }, [token, unstakeAnimal]);

  // handle Claim Earning
  const handleClaim = useCallback(async () => {
    if (!token) return;
    try {
      await claimStakingRewards(token);
      const result = avaliableStakedAnimals.map((animal) => {
        if (animal.mint === token.mint) {
          return {
            ...token,
            lastClaim: new Date(),
          }
        } else return animal;
      });
      setAvaliableStakedAnimals(result);
    } catch(e) {
      console.log('Claim Failed.');
    }
   
  }, [token, claimStakingRewards, avaliableStakedAnimals, setAvaliableStakedAnimals]);

  // pending rewords
  useEffect(() => {
    if (!token?.lastClaim || !stakingPeriod) return;
    if (token?.lastClaim && isStaked) {
      const redeem = getPendingStakingRewards(token, stakingPeriod);
      return () => {
        setRedeemable(redeem);
      };
    }
  }, [token, stakingPeriod, getPendingStakingRewards, isStaked]);


  const handleStakeClicked = () => {
    // const dismissModal: boolean = !!localStorage.getItem('dismissStakeModal');
    handleStake();
  }

  const handleClaimClicked = () => {
    // const dismissModal: boolean = !!localStorage.getItem('dismissClaimModal');
    // if (dismissModal) handleClaim();
    // else setClaimModalIsOpen(true);
    handleClaim();
  }

  return (
    <NftRoot>
      {token.uriData.image ? (
        <NftImg src={token.uriData.image} alt='' />
      ) : (
        <Skeleton.Avatar active shape='square' size={300} />
      )}

      <Title>{token.uriData.name}</Title>
      <MultiplierBox>
        <span>Rewards Per Day</span>{' '}
        <span>
          {(token?.emissionsPerDay || 0) / 10 ** 9}
          <span style={{ fontSize: 16 }}> $DAB</span>
        </span>
      </MultiplierBox>
      {token?.lastClaim && isStaked && (
        <MultiplierBox>
          <span>Pending Rewards</span> <span>{redeemable.toPrecision(5)}</span>
        </MultiplierBox>
      )}

      {/* { isLocked && isStaked &&(
        <MultiplierBox style={{flexWrap: "wrap"}}>
          <span style={{minWidth: "fit-content"}}>Claim {timeToUnlock}</span>
          <span style={{minWidth: "fit-content"}}>{dayjs(token?.lastClaim).add(3, "days").format('MM/DD/YY hh:mm a')}</span>
        </MultiplierBox>
      )} */}
      {/* { isStaked &&(
        <MultiplierBox style={{flexWrap: "wrap"}}>
          <span style={{minWidth: "fit-content"}}>Claim Now</span>
        </MultiplierBox>
      )} */}

      <ButtonGroup>
        {token?.lastClaim && isStaked ? (
          <>
           <>
            <Button onClick={handleClaimClicked} fixedWidth={true}>
                Claim Rewards
              </Button>
            </>
          <Button onClick={handleUnstake} fixedWidth={true}>
            Unstake NFT
          </Button>
          </>
        ) : (
          <Button onClick={handleStake} fixedWidth={true}>
            Stake NFT
          </Button>
        )}
      </ButtonGroup>
      <CustomModal
        modalIsOpen={isClaimModalOpen}
        setIsOpen={setClaimModalIsOpen}
        token={token}
        callback={handleClaim}
        title="Are you sure you want to claim?" 
        description={`If you claim $DAB now, you will not be able to unstake or claim rewards for the next 72 hours. If you want to claim & then unstake – simply click on "Unstake", which will automatically claim rewards as well.`}
        id="dismissClaimModal"
      />
      <CustomModal
        modalIsOpen={isStakeModalOpen}
        setIsOpen={setStakeModalIsOpen}
        token={token}
        callback={handleStake}
        title="Confirm Staking for 3 Days" 
        description="If you stake this NFT you will not be able to unstake for the next 3 days."
        id="dismissStakeModal"
      />
    </NftRoot>
  );
};

export default NftCard;
